/** Gallery wizard domain — Album → 3D Gallery upgrade */

export type WizardStepId =
  | 'intent'
  | 'scene'
  | 'frame'
  | 'color'
  | 'material'
  | 'prints'
  | 'matting'
  | 'review'

export const WIZARD_STEPS: readonly WizardStepId[] = [
  'intent',
  'scene',
  'frame',
  'color',
  'material',
  'prints',
  'matting',
  'review',
] as const

export const WIZARD_STEP_LABELS: Record<WizardStepId, string> = {
  intent: 'Get started',
  scene: 'Scene',
  frame: 'Frames',
  color: 'Color',
  material: 'Material',
  prints: 'Prints',
  matting: 'Matting',
  review: 'Review',
}

/** Short sub-label shown under each step in the wizard's step rail */
export const WIZARD_STEP_HINTS: Record<WizardStepId, string> = {
  intent: 'Why this gallery exists',
  scene: 'Pick a room',
  frame: 'Border style',
  color: 'Wall tone',
  material: 'Floor finish',
  prints: 'Choose your photos',
  matting: 'Border width',
  review: 'Final check',
}

export interface WizardAsset {
  id: string | number
  name: string
  thumbUrl: string
  /** Relative mesh/texture path for Babylon (Phase 2 apply) */
  assetPath?: string
}

export interface WizardColorOption {
  id: string
  name: string
  /** Representative swatch tone shown in the UI (picked for how it looks, not identity). */
  hex: string
  /**
   * The swatch's `hex[0]` from the raw catalog — this is the identity value
   * the renderer's interactive "pick a color, then click a wall" flow
   * matches against (`Assets.colors.find(i => i.hex[0] === selection.color)`
   * in renderer/main.js). Use this, not `hex`, when feeding a color
   * selection into that click-to-apply path.
   */
  applyHex: string
}

export interface WizardPrintSlot {
  index: number
  photoId?: string
  thumbUrl?: string
  url?: string
  egoId?: string
  excluded?: boolean
}

export type GalleryPrivacy = 'private' | 'public'

/** Sentinel — no frame applied (wizard + seed notes) */
export const FRAME_NONE = '__none__'

export interface WizardDraft {
  albumId: string
  step: WizardStepId
  sceneId?: string | number
  frameId?: string | number
  colorId?: string
  materialId?: string | number
  matWidth: number
  matColor: string
  slots: WizardPrintSlot[]
  galleryTitle: string
  privacy: GalleryPrivacy
}

export interface CreateGalleryFromAlbumResult {
  galleryId: string
  title: string
  printCount?: number
}
